import React, { useState } from 'react';
import { Plus, Trash2, Copy, Star, Sparkles, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { PageSection } from '../../../../types';

interface TestimonialsEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const TestimonialsEditor: React.FC<TestimonialsEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const items: any[] = content.items || [
    {
      id: '1',
      author: 'Mehmet Demir',
      role: 'Genel Müdür, ABC Holding',
      description: 'Hizmet kalitesi ve operasyonel hız gerçekten beklentilerimizin çok ötesindeydi.',
      rating: 5,
    },
  ];

  const [activeItemId, setActiveItemId] = useState<string | null>(items[0]?.id || null);

  const handleUpdateItem = (index: number, updates: any) => {
    const next = [...items];
    next[index] = { ...next[index], ...updates };
    onUpdateContent({ items: next });
  };

  const handleAddItem = () => {
    const newItem = {
      id: `t_${Date.now().toString(36)}`,
      author: 'Müşteri Adı',
      role: 'Pozisyon / Şirket',
      description: 'Deneyim ve görüş detayları...',
      rating: 5,
    };
    const next = [...items, newItem];
    onUpdateContent({ items: next });
    setActiveItemId(newItem.id);
  };

  const handleDeleteItem = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = items.filter((_, i) => i !== index);
    onUpdateContent({ items: next });
    if (activeItemId === items[index]?.id) {
      setActiveItemId(next[0]?.id || null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Headings & Layout */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Yorum Başlıkları & Düzen</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Sütun Düzeni</label>
          <div className="grid grid-cols-2 gap-2">
            {[2, 3].map((col) => {
              const currentCols = Number(content.columns) || 2;
              const isSelected = currentCols === col;
              return (
                <button
                  key={col}
                  type="button"
                  onClick={() => onUpdateContent({ columns: col })}
                  className={`py-1.5 px-3 rounded-xl text-xs font-bold transition border ${
                    isSelected
                      ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                      : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  {col} Sütunlu Izgara
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? 'MÜŞTERİ YORUMLARI'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'Müşterilerimizin Deneyimleri'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Testimonials List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-amber-400" />
            <h4 className="text-xs font-extrabold text-slate-200">Yorum Kartları ({items.length})</h4>
          </div>
          <button
            type="button"
            onClick={handleAddItem}
            className="px-2.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 shadow-sm transition"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Yorum Ekle</span>
          </button>
        </div>

        <div className="space-y-2.5">
          {items.map((item, idx) => {
            const isExpanded = activeItemId === item.id;

            return (
              <div
                key={item.id || idx}
                className={`rounded-2xl border transition-all ${
                  isExpanded
                    ? 'bg-slate-800/90 border-amber-500/50 shadow-md ring-1 ring-amber-500/20'
                    : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
                }`}
              >
                <div
                  onClick={() => setActiveItemId(isExpanded ? null : item.id)}
                  className="p-3 flex items-center justify-between cursor-pointer select-none"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-xs">
                      {item.author?.charAt(0) || 'M'}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-200 truncate">{item.author}</div>
                      <div className="text-[10px] text-slate-400 truncate">{item.role || 'Müşteri'}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => handleDeleteItem(idx, e)}
                      title="Sil"
                      className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="p-1 text-slate-400">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-4 pt-2 border-t border-slate-700/50 space-y-3.5">
                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Kişi / Kurum Adı</label>
                        <input
                          type="text"
                          value={item.author || ''}
                          onChange={(e) => handleUpdateItem(idx, { author: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ünvan / Şirket</label>
                        <input
                          type="text"
                          value={item.role || ''}
                          onChange={(e) => handleUpdateItem(idx, { role: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Yorum Başlığı (Opsiyonel)</label>
                        <input
                          type="text"
                          value={item.title || ''}
                          onChange={(e) => handleUpdateItem(idx, { title: e.target.value })}
                          placeholder="Örn: Harika Deneyim"
                          className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Avatar Görsel URL (Opsiyonel)</label>
                        <input
                          type="text"
                          value={item.avatarUrl || ''}
                          onChange={(e) => handleUpdateItem(idx, { avatarUrl: e.target.value })}
                          placeholder="https://..."
                          className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Yorum / Referans Metni</label>
                      <textarea
                        value={item.description || ''}
                        onChange={(e) => handleUpdateItem(idx, { description: e.target.value })}
                        rows={3}
                        className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2 rounded-xl outline-none focus:border-blue-500 resize-none"
                      />
                    </div>

                    {/* Star Rating */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Puan (Yıldız)</label>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => handleUpdateItem(idx, { rating: star })}
                            className="p-1 text-slate-600 hover:text-amber-400 transition"
                          >
                            <Star
                              className={`w-5 h-5 ${
                                star <= (item.rating || 5)
                                  ? 'text-amber-400 fill-amber-400'
                                  : 'text-slate-700'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
